// src/components/AIChatMessage.jsx
// Chat message bubble — user (right) or assistant (left).
// Safe markdown: **bold**, `code`, newlines, bullet lists — no dangerouslySetInnerHTML.

import React from 'react';
import AIAssistantAvatar from './AIAssistantAvatar';

// Parse inline markdown into React elements (safe — no raw HTML injection)
function parseInline(text) {
  if (!text) return null;
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Bold: **text**
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    // Code: `text`
    const codeMatch = remaining.match(/`([^`]+)`/);

    // Find earliest match
    const boldIdx = boldMatch ? remaining.indexOf(boldMatch[0]) : Infinity;
    const codeIdx = codeMatch ? remaining.indexOf(codeMatch[0]) : Infinity;

    if (boldIdx === Infinity && codeIdx === Infinity) {
      // No more matches — push remaining text
      parts.push(<span key={key++}>{remaining}</span>);
      break;
    }

    const earliestIdx = Math.min(boldIdx, codeIdx);
    // Text before the match
    if (earliestIdx > 0) {
      parts.push(<span key={key++}>{remaining.slice(0, earliestIdx)}</span>);
    }

    if (boldIdx <= codeIdx && boldMatch) {
      parts.push(
        <strong key={key++} className="font-semibold text-slate-900 dark:text-slate-100">
          {boldMatch[1]}
        </strong>
      );
      remaining = remaining.slice(boldIdx + boldMatch[0].length);
    } else if (codeMatch) {
      parts.push(
        <code key={key++} className="bg-slate-100 dark:bg-slate-700/70 text-slate-900 dark:text-slate-100 px-1 py-0.5 rounded text-[12px] font-mono">
          {codeMatch[1]}
        </code>
      );
      remaining = remaining.slice(codeIdx + codeMatch[0].length);
    }
  }

  return parts;
}

// Render markdown text as React elements (safe — no innerHTML)
function renderMarkdown(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    // Bullet points: • or - at start of line
    const isBullet = /^[•\-]\s/.test(line);
    if (isBullet) {
      const bulletText = line.replace(/^[•\-]\s/, '');
      return (
        <div key={i} className="flex gap-1.5 ml-2">
          <span className="text-slate-500 select-none">•</span>
          <span>{parseInline(bulletText)}</span>
        </div>
      );
    }
    // Empty line → spacing
    if (!line.trim()) return <div key={i} className="h-2" />;
    return <div key={i}>{parseInline(line)}</div>;
  });
}

function formatTime(timestamp) {
  if (!timestamp) return '';
  const d = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export default function AIChatMessage({ role, content, timestamp, showAvatar = true }) {
  const isUser = role === 'user';

  // Assistant message: render with optional avatar to the left.
  // Avatar is hidden on consecutive assistant messages — the parent passes
  // showAvatar=false when the previous message was also from the assistant,
  // so a long answer with multiple bubbles stays visually grouped.
  if (!isUser) {
    return (
      <div className="flex items-end gap-2 max-w-[92%] self-start">
        {showAvatar ? (
          <AIAssistantAvatar size="sm" />
        ) : (
          <div className="w-6 flex-shrink-0" aria-hidden="true" />
        )}
        <div className="flex flex-col gap-1 min-w-0">
          <div className="px-3.5 py-2.5 rounded-2xl rounded-bl-md text-sm leading-relaxed bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 shadow-sm shadow-slate-900/5 dark:shadow-black/20">
            {renderMarkdown(content)}
          </div>
          {timestamp && (
            <span className="text-[11px] text-slate-500 dark:text-slate-500 px-1">
              {formatTime(timestamp)}
            </span>
          )}
        </div>
      </div>
    );
  }

  // User message: right-aligned, no avatar.
  return (
    <div className="flex flex-col gap-1 max-w-[88%] self-end">
      <div className="px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed bg-indigo-600 text-white shadow-sm shadow-indigo-600/20">
        {content}
      </div>
      {timestamp && (
        <span className="text-[11px] text-slate-500 dark:text-slate-500 px-1 text-right">
          {formatTime(timestamp)}
        </span>
      )}
    </div>
  );
}
